import { concatBytes } from '../escpos/commands';
import type { XpP203aLabelConfig } from '../xpP203aLabelConfig';

/** TSPL / TSPL2 helpers — CRLF-terminated ASCII command jobs with inline binary BITMAP data. */

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: false });

/** Standard label job header (SIZE, GAP, direction, speed, density, CLS). */
export function buildTsplLabelHeaderLines(cfg: XpP203aLabelConfig): string[] {
  return [
    `SIZE ${cfg.widthMm} mm,${cfg.heightMm} mm`,
    `GAP ${cfg.gapMm} mm, 0 mm`,
    `DIRECTION ${cfg.direction}`,
    'REFERENCE 0,0',
    `SPEED ${cfg.speed}`,
    `DENSITY ${cfg.density}`,
    'CLS',
  ];
}

export function escapeTsplQuotedText(value: string): string {
  return String(value ?? '')
    .replace(/\r\n/g, ' ')
    .replace(/\n/g, ' ')
    .replace(/\r/g, ' ')
    .replace(/"/g, '\\"')
    .trim();
}

/** Inline BITMAP with raw raster bytes (TSPL spec — not hex ASCII). */
export type TsplBitmapPart = {
  kind: 'bitmap';
  x: number;
  y: number;
  bytesPerRow: number;
  heightPx: number;
  rows: Uint8Array;
};

export type TsplJobPart = string | TsplBitmapPart;

export function tsplBitmapPart(
  x: number,
  y: number,
  bytesPerRow: number,
  heightPx: number,
  rows: Uint8Array,
): TsplBitmapPart {
  return { kind: 'bitmap', x, y, bytesPerRow, heightPx, rows };
}

function encodeBitmapPart(part: TsplBitmapPart): Uint8Array {
  const expected = part.bytesPerRow * part.heightPx;
  const rows =
    part.rows.length >= expected ? part.rows.subarray(0, expected) : concatBytes(part.rows, new Uint8Array(expected - part.rows.length));
  const header = encoder.encode(
    `BITMAP ${part.x},${part.y},${part.bytesPerRow},${part.heightPx},0,`,
  );
  return concatBytes(header, rows, encoder.encode('\r\n'));
}

/** Build a complete TSPL job from command lines and optional binary bitmap parts. */
export function buildTsplJob(parts: TsplJobPart[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  for (const part of parts) {
    if (typeof part === 'string') {
      const t = part.trim();
      if (!t) continue;
      chunks.push(encoder.encode(`${t}\r\n`));
    } else {
      chunks.push(encodeBitmapPart(part));
    }
  }
  return concatBytes(...chunks);
}

/** Human-readable preview for logs (binary BITMAP regions shown as `<bitmap NxM>`). */
export function previewTsplBytes(bytes: Uint8Array, maxChars = 400): string {
  if (bytes.length === 0) return '(empty)';
  try {
    const sanitized = sanitizeTsplBytesForPreview(bytes);
    const text = decoder.decode(sanitized);
    const normalized = text.replace(/\r\n/g, '\\n').replace(/\n/g, '\\n');
    if (normalized.length <= maxChars) {
      return `${bytes.length}B "${normalized}"`;
    }
    return `${bytes.length}B "${normalized.slice(0, maxChars)}…"`;
  } catch {
    return `${bytes.length}B (decode failed)`;
  }
}

/** Replace binary BITMAP payloads with placeholders for debug preview. */
export function sanitizeTsplBytesForPreview(bytes: Uint8Array): Uint8Array {
  const out: number[] = [];
  let i = 0;
  while (i < bytes.length) {
    const parsed = parseBinaryBitmapAt(bytes, i);
    if (parsed) {
      const headerText = decoder.decode(bytes.subarray(i, parsed.binaryStart));
      for (const c of headerText) out.push(c.charCodeAt(0));
      const placeholder = `<bitmap ${parsed.bytesPerRow}x${parsed.heightPx}B>`;
      for (const c of placeholder) out.push(c.charCodeAt(0));
      i = parsed.end;
      continue;
    }

    const crlf = findCrlfAt(bytes, i);
    if (crlf >= 0) {
      for (let j = i; j <= crlf + 1; j += 1) out.push(bytes[j]!);
      i = crlf + 2;
    } else {
      out.push(bytes[i]!);
      i += 1;
    }
  }
  return new Uint8Array(out);
}

function findCrlfAt(data: Uint8Array, from: number): number {
  for (let i = from; i < data.length - 1; i += 1) {
    if (data[i] === 0x0d && data[i + 1] === 0x0a) return i;
  }
  return -1;
}

/** Parsed binary BITMAP command starting at `pos`, or null. */
export function parseBinaryBitmapAt(
  data: Uint8Array,
  pos: number,
): { binaryStart: number; bytesPerRow: number; heightPx: number; end: number } | null {
  const prefix = encoder.encode('BITMAP ');
  if (pos + prefix.length > data.length) return null;
  for (let j = 0; j < prefix.length; j += 1) {
    if (data[pos + j] !== prefix[j]) return null;
  }

  let p = pos + prefix.length;
  const nums: number[] = [];
  let numStart = p;
  while (p < data.length && nums.length < 5) {
    const b = data[p]!;
    if (b === 0x2c) {
      const n = Number.parseInt(decoder.decode(data.subarray(numStart, p)).trim(), 10);
      if (!Number.isFinite(n)) return null;
      nums.push(n);
      numStart = p + 1;
      p += 1;
      continue;
    }
    if (b === 0x0d || b === 0x0a) return null;
    p += 1;
  }
  if (nums.length !== 5) return null;

  const bytesPerRow = nums[2]!;
  const heightPx = nums[3]!;
  if (bytesPerRow <= 0 || heightPx <= 0) return null;

  const binaryStart = numStart;
  const binaryLen = bytesPerRow * heightPx;
  const binaryEnd = binaryStart + binaryLen;
  if (binaryEnd > data.length) return null;

  let end = binaryEnd;
  if (data[binaryEnd] === 0x0d && data[binaryEnd + 1] === 0x0a) {
    end = binaryEnd + 2;
  } else if (data[binaryEnd] === 0x0a) {
    end = binaryEnd + 1;
  }

  return { binaryStart, bytesPerRow, heightPx, end };
}

export function tsplTextLine(
  x: number,
  y: number,
  text: string,
  opts?: { font?: string; xMul?: number; yMul?: number },
): string {
  const font = opts?.font ?? '3';
  const xMul = opts?.xMul ?? 1;
  const yMul = opts?.yMul ?? 1;
  return `TEXT ${x},${y},"${font}",0,${xMul},${yMul},"${escapeTsplQuotedText(text)}"`;
}

export function tsplQrLine(x: number, y: number, payload: string, cellWidth = 4): string {
  return `QRCODE ${x},${y},L,${cellWidth},A,0,"${escapeTsplQuotedText(payload)}"`;
}
